import cx from "classnames"
import React, { createContext, useContext } from "react"
import { Popover } from "@base-ui/react/popover"
import { popupTransition } from "../utils/popup-transition"
import { NavigationGroupItemLine } from "./navigation-group-item-line"
import { NavigationBadge, type NavigationBadgeType } from "./navigation-badge"

// =============================================================================
// Handle + context
// =============================================================================

export type NavigationFlyoutPayload = {
  label: string
  badge?: NavigationBadgeType
  items: React.ReactNode
}

export type NavigationFlyoutHandle = Popover.Handle<NavigationFlyoutPayload>

const NavigationFlyoutContext = createContext<NavigationFlyoutHandle | null>(
  null
)

export function useNavigationFlyoutHandle(): NavigationFlyoutHandle | null {
  return useContext(NavigationFlyoutContext)
}

export const NavigationFlyoutProvider = NavigationFlyoutContext.Provider

// =============================================================================
// Styles
// =============================================================================

// Connector radius comes from the design's 12px ellipse. All other dimensions
// are derived from it so there is a single value to update if the design changes.
const CONNECTOR_RADIUS = 12
// How far each connector reaches above/below the popup box.
const CONNECTOR_OFFSET = CONNECTOR_RADIUS * 2 + 1
const connectorVars = {
  "--connector-radius": `${CONNECTOR_RADIUS}px`,
  "--connector-height": `${CONNECTOR_RADIUS * 2}px`,
  "--connector-offset": `${CONNECTOR_OFFSET}px`,
  "--connector-fill-depth": `${CONNECTOR_RADIUS + 1}px`,
  "--flyout-viewport-padding": "0.25rem" // matches p-1
} as React.CSSProperties

const bottomConnectorStyles = [
  "before:content-[''] before:absolute",
  "before:w-[var(--connector-radius)] before:h-[var(--connector-height)]",
  "before:bottom-[calc(0px_-_var(--connector-offset))] before:left-0",
  "before:rounded-tl-[var(--connector-radius)]",
  "before:border-l before:border-gray-200",
  "before:shadow-[0_-1px_0_0_var(--gray-200),0_calc(0px_-_var(--connector-fill-depth))_0_0_var(--warm-white-100)]"
].join(" ")

const topConnectorStyles = [
  "after:content-[''] after:absolute",
  "after:w-[var(--connector-radius)] after:h-[var(--connector-height)]",
  "after:top-[calc(0px_-_var(--connector-offset))] after:left-0",
  "after:rounded-bl-[var(--connector-radius)]",
  "after:border-l after:border-gray-200",
  "after:shadow-[0_1px_0_0_var(--gray-200),0_var(--connector-fill-depth)_0_0_var(--warm-white-100)]"
].join(" ")

// Panel surface — applied to Popover.Popup so the border, background, and
// connectors slide and morph as a single unit when re-anchoring. The auto-
// resize CSS variables (--popup-width / --popup-height) are populated by
// Popover.Viewport's resize observer; falling back to `auto` keeps the popup
// sized to its content on first open before the observer has run.
const popupStyles = cx(
  "relative",
  "w-(--popup-width,auto) h-(--popup-height,auto)",
  "bg-warm-white-100",
  // Round only the right corners — the left edge is flush with the sidebar.
  "rounded-tr-xl rounded-br-xl",
  // No left border — the sidebar edge acts as the left boundary.
  "border border-l-0 border-gray-200",
  // Entry/exit transition, right side only since anchor is always on the right
  popupTransition({ sides: "r" }),
  bottomConnectorStyles,
  topConnectorStyles
)

// Slide between trigger anchors. BaseUI updates inline top/left/transform on
// the Positioner each time the active trigger changes; transitioning those
// properties produces the visual "panel travels" effect rather than teleport.
const positionerStyles = cx(
  "transition-[top,left,right,bottom,translate,transform]",
  "duration-[150ms] ease-out",
  // On the very first open after mount (or after the nav was collapsed and the
  // triggers re-registered), BaseUI mounts the Positioner with `opacity: 0` and
  // no top/left, then updates top/left once the anchor is computed. Without
  // suppression the position transition fires on that first update and the
  // panel slides in diagonally from (0, 0). The Popup gets `data-starting-style`
  // for exactly that frame, so :has() lets the Positioner detect "initial
  // open" and skip the position transition until the panel is anchored.
  "has-[[data-starting-style]]:transition-none"
)

// Cross-fade content on trigger swap. Popover.Viewport wraps its children in
// `<div data-current>` and, during a transition, also renders `<div data-previous>`
// containing a DOM clone of the prior content. The activation-direction is
// "<horizontal> <vertical>" — for our vertically-stacked triggers the horizontal
// part collapses to empty and we get " up" or " down". Tailwind's ~= matcher
// keys off whitespace-separated tokens, so `data-[activation-direction~='down']`
// matches " down" correctly.
const viewportStyles = cx(
  "relative overflow-clip",
  "p-(--flyout-viewport-padding)",
  // Pin the inner wrappers to the popup's *target* content width so items
  // render at full width during the size animation. Without this, the
  // wrappers inherit the popup's currently-animating width and items'
  // `truncate` flashes an ellipsis until the animation finishes.
  // Subtracting 2× the viewport padding converts --popup-width (the popup's
  // offsetWidth) to the viewport's content width, so item backgrounds stay
  // flush with the padding rather than bleeding edge to edge.
  // BaseUI overrides --popup-width inline on the [data-previous] wrapper
  // to the previous content's width, so this same selector works for both.
  "[&_[data-current]]:w-[calc(var(--popup-width)_-_2_*_var(--flyout-viewport-padding))]",
  "[&_[data-previous]]:w-[calc(var(--popup-width)_-_2_*_var(--flyout-viewport-padding))]",
  // Both the incoming and outgoing wrappers share the same transition.
  "[&_[data-current]]:transition-[translate,opacity]",
  "[&_[data-current]]:duration-[150ms]",
  "[&_[data-current]]:ease-out",
  "[&_[data-previous]]:transition-[translate,opacity]",
  "[&_[data-previous]]:duration-[150ms]",
  "[&_[data-previous]]:ease-out",
  // 'down': panel moves to a lower trigger. New content slides upward into
  // place; old content slides upward out.
  "data-[activation-direction~='down']:[&_[data-current][data-starting-style]]:translate-y-2",
  "data-[activation-direction~='down']:[&_[data-current][data-starting-style]]:opacity-0",
  "data-[activation-direction~='down']:[&_[data-previous][data-ending-style]]:-translate-y-2",
  "data-[activation-direction~='down']:[&_[data-previous][data-ending-style]]:opacity-0",
  // 'up': mirror.
  "data-[activation-direction~='up']:[&_[data-current][data-starting-style]]:-translate-y-2",
  "data-[activation-direction~='up']:[&_[data-current][data-starting-style]]:opacity-0",
  "data-[activation-direction~='up']:[&_[data-previous][data-ending-style]]:translate-y-2",
  "data-[activation-direction~='up']:[&_[data-previous][data-ending-style]]:opacity-0"
)

const headingStyles = [
  "flex h-10 items-center gap-x-2 p-2",
  "text-sm leading-6 font-semibold text-gray-900",
  "whitespace-nowrap"
].join(" ")

const itemListStyles = [
  // this is needed because we have global styles for ul in application.css
  "list-none p-0"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * The single flyout panel shared by every collapsed `Navigation.Group` on the
 * page. Each group registers itself as a `Popover.Trigger` against the same
 * handle and pushes its label + items as the trigger's payload. As the user
 * moves between group icons, BaseUI re-anchors this single popover and
 * `Popover.Viewport` exposes a `data-activation-direction` attribute so the
 * panel animates between positions instead of close-then-reopen.
 *
 * The animation has three layers, all driven by BaseUI:
 *  - Positioner slides between trigger anchors (top/left transition).
 *  - Popup morphs in size as content changes (width/height transition driven
 *    by --popup-width / --popup-height CSS variables set by the Viewport).
 *  - Viewport cross-fades the inner content using data-current / data-previous
 *    selectors, with direction taken from data-activation-direction.
 *
 * Rendered once by `Navigation`. Internal — not part of the public API.
 */
export function NavigationFlyout({
  handle
}: {
  handle: NavigationFlyoutHandle
}) {
  return (
    <Popover.Root handle={handle}>
      {({ payload }) => (
        <Popover.Portal keepMounted>
          {/* sideOffset={8} matches the nav's px-2 padding so the flyout is
           * flush with the sidebar's right edge rather than the trigger's.
           *
           * alignOffset={4} matches the popup's padding so the label in the
           * flyout is aligned with the sidebar group icon
           *
           * The side never flips (the panel must stay attached to the sidebar
           * edge), but the panel shifts up on short viewports so it never
           * overflows the page and creates an outer scrollbar. The connectors
           * hang outside the popup box, so the collision padding reserves room
           * for them.
           */}
          <Popover.Positioner
            side="right"
            sideOffset={8}
            align="start"
            alignOffset={-4}
            collisionAvoidance={{
              side: "none",
              align: "shift",
              fallbackAxisSide: "none"
            }}
            collisionPadding={{
              top: CONNECTOR_OFFSET,
              bottom: CONNECTOR_OFFSET
            }}
            className={positionerStyles}
          >
            <Popover.Popup
              data-slot="navigation-group-flyout"
              initialFocus={false}
              finalFocus={false}
              className={popupStyles}
              style={connectorVars}
            >
              <Popover.Viewport className={viewportStyles}>
                <div className={headingStyles}>
                  {payload?.label}
                  {payload?.badge && <NavigationBadge type={payload.badge} />}
                </div>
                <div className="relative">
                  <NavigationGroupItemLine />
                  <ul
                    className={itemListStyles}
                    data-slot="navigation-group-flyout-items"
                  >
                    {payload?.items}
                  </ul>
                </div>
              </Popover.Viewport>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      )}
    </Popover.Root>
  )
}
